import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';
import { useSEO } from '../hooks/useSEO';
import PageHero from '../components/PageHero';
import { Newspaper, Play, X, ChevronLeft, ChevronRight, Film, Radio, Tv, ExternalLink } from 'lucide-react';
import { EditRegion } from '../components/AdminEditBar';

// Real press clippings & videos from https://yekuv.org/basinda-yekuv/
const PRESS_CLIPS = [
  { id: 'sabah', src: '/media/sabah.jpg', outlet: 'Sabah Gazetesi', title: 'YEKÜV Haberi', year: '2024', span: 'col-span-2 row-span-2' },
  { id: 'hello', src: '/media/hello.jpg', outlet: 'Hello! Türkiye', title: 'Yardımseverlik Röportajı', year: '2024', span: '' },
  { id: 'samdan', src: '/media/samdan.jpg', outlet: 'Şamdan Plus', title: 'Eğitim Özel Sayfası', year: '2024', span: '' },
  { id: 'doc-1', src: '/media/doc-2024-03-26.jpg', outlet: 'Ulusal Gazete', title: 'YEKÜV Bağış Haberi', year: '2024', span: 'row-span-2' },
  { id: 'doc-2', src: '/media/doc-2024-03-20.jpg', outlet: 'Ulusal Gazete', title: 'YEKÜV Eğitim Haberi', year: '2024', span: '' },
  { id: 'new-1', src: '/media/new-1.png', outlet: 'Basın Bülteni', title: 'Özel Sayfa', year: '2024', span: '' },
  { id: 'new-2', src: '/media/new-2.png', outlet: 'Basın Bülteni', title: 'Özel Sayfa', year: '2024', span: '' },
  { id: 'p001', src: '/media/press-001.jpg', outlet: 'YEKÜV 30. Yıl', title: 'Basın Dosyası #1', year: '2023', span: '' },
  { id: 'p002', src: '/media/press-002.jpg', outlet: 'YEKÜV 30. Yıl', title: 'Basın Dosyası #2', year: '2023', span: '' },
  { id: 'p003', src: '/media/press-003.jpg', outlet: 'YEKÜV 30. Yıl', title: 'Basın Dosyası #3', year: '2023', span: '' },
  { id: 'p004', src: '/media/press-004.jpg', outlet: 'YEKÜV 30. Yıl', title: 'Basın Dosyası #4', year: '2023', span: '' },
  { id: 'p005', src: '/media/press-005.jpg', outlet: 'YEKÜV 30. Yıl', title: 'Basın Dosyası #5', year: '2023', span: '' },
];

const VIDEOS = [
  {
    id: 'rcbLn8G1wYA',
    title: 'YEKÜV 30 Yıl Haberi',
    subtitle: 'CNN Türk Haber Programı',
    outlet: 'CNN Türk',
    year: '2022',
    featured: true,
  },
  {
    id: 'LIvbsloXS0Q',
    title: 'YEKÜV 30. YIL Gecesi',
    subtitle: 'Kuruluşumuzun 30. Yıl Kutlama Gecesi',
    outlet: 'YEKÜV',
    year: '2022',
    featured: false,
  },
  {
    id: 'f0V0jxa27J4',
    title: 'Işığım Olur musun?',
    subtitle: 'Ödül Töreni — 31 Ekim 2019',
    outlet: 'YEKÜV',
    year: '2019',
    featured: false,
  },
];

const STATS = [
  { n: '12+', label: 'Basın Yansıması', Icon: Newspaper },
  { n: '3', label: 'TV Haberi', Icon: Tv },
  { n: '33', label: 'Yıllık Haber Arşivi', Icon: Radio },
  { n: '25K+', label: 'Toplam Erişim', Icon: Film },
];

export default function MediaMentions() {
  useSEO('media');
  const [lightbox, setLightbox] = useState(null); // { type: 'image' | 'video', idx }
  const [videoId, setVideoId] = useState(null);
  const [clips, setClips] = useState(PRESS_CLIPS);
  const [videos, setVideos] = useState(VIDEOS);

  useEffect(() => {
    // Pull live records from admin (press_clips collection); fall back to mock when empty
    api.publicContent('press_clips').then(list => {
      if (!Array.isArray(list) || list.length === 0) return;
      const items = list.map(it => ({ dbid: it.id, ...(it.data || {}) }));
      const vids = items.filter(x => (x.type === 'video') || String(x.video_id || '').trim());
      const imgs = items.filter(x => !vids.includes(x));
      if (imgs.length > 0) {
        setClips(imgs.map(x => ({ id: x.dbid, src: x.image, outlet: x.outlet || 'YEKÜV', title: x.title, year: x.year || '', span: '' })));
      }
      if (vids.length > 0) {
        setVideos(vids.map((x, i) => ({
          id: (x.video_id || '').trim(), title: x.title, subtitle: x.outlet || '',
          outlet: x.outlet || 'YEKÜV', year: x.year || '', featured: i === 0, cover: x.image || '',
        })));
      }
    }).catch(() => {});
  }, []);

  const openImage = (idx) => setLightbox({ type: 'image', idx });
  const closeLightbox = () => setLightbox(null);

  const nextImage = useCallback(() => setLightbox((l) => l ? { ...l, idx: (l.idx + 1) % clips.length } : null), [clips.length]);
  const prevImage = useCallback(() => setLightbox((l) => l ? { ...l, idx: (l.idx - 1 + clips.length) % clips.length } : null), [clips.length]);

  useEffect(() => {
    if (!lightbox && !videoId) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') { closeLightbox(); setVideoId(null); }
      if (lightbox?.type === 'image') {
        if (e.key === 'ArrowRight') nextImage();
        if (e.key === 'ArrowLeft') prevImage();
      }
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleKey);
    };
  }, [lightbox, videoId, nextImage, prevImage]);

  const featured = videos.find(v => v.featured);
  const otherVideos = videos.filter(v => !v.featured);

  return (
    <EditRegion label="Medyada YEKÜV" to="/admin/press">
      <>
      <PageHero slug="media"
        kicker="Medyada YEKÜV"
        title={<>Basındaki <span className="italic text-coral-200">yansımalarımız</span>.</>}
        text="33 yıllık eğitim yolculuğumuzun ulusal medyadaki izdüşümü. Gazete kesitleri, röportajlar ve televizyon haberlerinden seçki."
      />

      {/* Stats strip */}
      <section className="bg-cream border-b border-ink-100">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {STATS.map((s, i) => (
              <div key={i} className="flex items-center gap-4" data-testid={`media-stat-${i}`}>
                <div className="w-12 h-12 rounded-xl bg-ink-600 text-gold grid place-items-center shrink-0">
                  <s.Icon className="w-5 h-5" />
                </div>
                <div>
                  <div className="font-display text-3xl md:text-4xl text-ink-600 tabular leading-none">{s.n}</div>
                  <div className="text-[11px] tracking-[0.25em] uppercase text-ink-400 font-semibold mt-1.5">{s.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured video — CNN Türk 30. Yıl Haberi */}
      <section className="relative bg-ink-600 text-cream overflow-hidden">
        <div className="absolute inset-0 opacity-[0.07] pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle at 20% 20%, #C9A96E 0, transparent 50%), radial-gradient(circle at 80% 80%, #E8826F 0, transparent 50%)' }} />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 py-20 md:py-28">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-5">
              <div className="inline-flex items-center gap-2 text-[11px] tracking-[0.3em] uppercase text-gold font-semibold">
                <Tv className="w-3.5 h-3.5" /> Öne Çıkan Haber
              </div>
              <h2 className="font-display text-4xl md:text-5xl lg:text-[64px] mt-5 leading-[1.0]">
                {featured.title}<span className="text-coral-200 italic">.</span>
              </h2>
              <p className="mt-5 text-cream/75 text-lg leading-relaxed max-w-lg">{featured.subtitle}</p>
              <div className="mt-8 flex items-center gap-6">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.3em] text-gold font-semibold">Kaynak</div>
                  <div className="font-display text-xl mt-1">{featured.outlet}</div>
                </div>
                <div className="h-10 w-px bg-cream/20" />
                <div>
                  <div className="text-[10px] uppercase tracking-[0.3em] text-gold font-semibold">Yıl</div>
                  <div className="font-display text-xl tabular mt-1">{featured.year}</div>
                </div>
              </div>
              <button
                onClick={() => setVideoId(featured.id)}
                data-testid="featured-video-play"
                className="mt-10 inline-flex items-center gap-3 bg-gold text-ink-700 rounded-full px-6 h-14 font-semibold hover:bg-gold-400 transition-all hover:gap-4 group"
              >
                <span className="w-9 h-9 rounded-full bg-ink-700 text-gold grid place-items-center group-hover:scale-110 transition-transform">
                  <Play className="w-4 h-4" fill="currentColor" />
                </span>
                Videoyu İzle
              </button>
            </div>

            <div className="lg:col-span-7">
              <button
                onClick={() => setVideoId(featured.id)}
                className="group relative block w-full aspect-video rounded-[28px] overflow-hidden shadow-2xl shadow-ink-900/50"
              >
                <img
                  src={`https://img.youtube.com/vi/${featured.id}/maxresdefault.jpg`}
                  alt={featured.title}
                  className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-700"
                  onError={(e) => { e.currentTarget.src = `https://img.youtube.com/vi/${featured.id}/hqdefault.jpg`; }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-ink-900/70 via-ink-900/20 to-transparent" />
                <div className="absolute inset-0 grid place-items-center">
                  <div className="w-24 h-24 rounded-full bg-gold/95 text-ink-700 grid place-items-center shadow-2xl group-hover:scale-110 transition-transform">
                    <Play className="w-10 h-10 ml-1" fill="currentColor" />
                  </div>
                </div>
                <div className="absolute top-5 left-5 inline-flex items-center gap-1.5 bg-red-600 text-white rounded-full px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.2em]">
                  <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" /> HABER
                </div>
                <div className="absolute bottom-5 right-5 text-cream/70 text-xs tabular bg-ink-900/60 backdrop-blur px-3 py-1.5 rounded-full">
                  YouTube · {featured.outlet}
                </div>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Other videos */}
      <section className="bg-cream">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 md:py-24">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
            <div>
              <div className="text-[11px] tracking-[0.3em] uppercase text-coral-300 font-semibold">Video Arşivi</div>
              <h2 className="font-display text-4xl md:text-5xl text-ink-600 mt-3 leading-tight">
                Kamera arkasındaki <span className="italic text-coral-300">anlar</span>.
              </h2>
            </div>
            <a
              href="https://www.youtube.com/channel/UCGUY_2hFVMkOVoNvXhji7CQ"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-ink-600 font-semibold text-sm hover:gap-3 transition-all"
              data-testid="youtube-channel-link"
            >
              YouTube kanalımız <ExternalLink className="w-4 h-4" />
            </a>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {otherVideos.map((v, i) => (
              <button
                key={v.id}
                onClick={() => setVideoId(v.id)}
                data-testid={`video-card-${i}`}
                className="group relative aspect-video rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl hover:shadow-ink-900/20 transition-shadow"
              >
                <img
                  src={`https://img.youtube.com/vi/${v.id}/maxresdefault.jpg`}
                  alt={v.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  onError={(e) => { e.currentTarget.src = `https://img.youtube.com/vi/${v.id}/hqdefault.jpg`; }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-ink-900 via-ink-900/40 to-transparent" />

                {/* Play button */}
                <div className="absolute inset-0 grid place-items-center">
                  <div className="w-16 h-16 rounded-full bg-gold/95 text-ink-700 grid place-items-center shadow-xl group-hover:scale-110 transition-transform">
                    <Play className="w-6 h-6 ml-0.5" fill="currentColor" />
                  </div>
                </div>

                {/* Caption */}
                <div className="absolute bottom-0 left-0 right-0 p-6 text-cream text-left">
                  <div className="text-[10px] uppercase tracking-[0.3em] text-gold font-semibold">{v.outlet} · {v.year}</div>
                  <div className="font-display text-2xl md:text-3xl mt-2 leading-tight">{v.title}</div>
                  <div className="text-cream/70 text-sm mt-1.5">{v.subtitle}</div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Press clippings — editorial masonry */}
      <section className="bg-cream-200 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none select-none opacity-[0.04]" aria-hidden="true">
          <div className="absolute top-16 -left-10 font-display text-[200px] md:text-[280px] leading-none text-ink-600 font-bold tracking-tighter italic">PRESS</div>
        </div>

        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 py-20 md:py-28">
          <div className="grid lg:grid-cols-12 gap-10 items-end mb-14">
            <div className="lg:col-span-7">
              <div className="text-[11px] tracking-[0.3em] uppercase text-coral-300 font-semibold">Gazete Kesitleri</div>
              <h2 className="font-display text-4xl md:text-5xl lg:text-[64px] text-ink-600 mt-4 leading-[1.0]">
                Sayfa <span className="italic text-coral-300">sayfa</span> hikâyemiz.
              </h2>
            </div>
            <div className="lg:col-span-5 text-ink-500 leading-relaxed">
              Ulusal ve yerel basındaki röportajlarımızdan, bağış törenlerimizden ve eğitim faaliyetlerimizden kesitler. Büyütmek için görsele tıklayın.
            </div>
          </div>

          {/* Editorial grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 auto-rows-[200px] gap-4">
            {clips.map((clip, idx) => (
              <button
                key={clip.id}
                onClick={() => openImage(idx)}
                data-testid={`press-clip-${idx}`}
                className={`group relative overflow-hidden rounded-2xl bg-ink-50 border border-ink-100 hover:border-gold transition-all hover:-translate-y-1 hover:shadow-xl ${clip.span}`}
              >
                <img
                  src={clip.src}
                  alt={clip.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  loading="lazy"
                />
                {/* Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-ink-900/90 via-ink-900/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                <div className="absolute inset-x-0 bottom-0 p-4 text-cream text-left translate-y-2 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all">
                  <div className="text-[9px] uppercase tracking-[0.3em] text-gold font-bold">{clip.outlet}</div>
                  <div className="font-display text-base mt-1 leading-tight">{clip.title}</div>
                  <div className="text-cream/70 text-[11px] tabular mt-1">{clip.year}</div>
                </div>

                {/* Corner year tag (always visible) */}
                <div className="absolute top-3 right-3 bg-cream/95 backdrop-blur text-ink-600 text-[10px] font-bold tabular px-2.5 py-1 rounded-full tracking-wider">
                  {clip.year}
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-cream">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 md:py-20">
          <div className="relative overflow-hidden rounded-[32px] bg-ink-600 text-cream p-10 md:p-14 grid md:grid-cols-2 gap-8 items-center">
            <div className="absolute -top-24 -right-24 w-[400px] h-[400px] rounded-full bg-gold/15 blur-3xl pointer-events-none" />
            <div className="relative">
              <div className="text-[11px] tracking-[0.3em] uppercase text-gold font-semibold">Basın</div>
              <h3 className="font-display text-3xl md:text-4xl lg:text-5xl mt-3 leading-tight">Basın mensuplarının dikkatine.</h3>
              <p className="mt-4 text-cream/75">Röportaj ve haber talepleriniz için iletişim bürosuyla doğrudan iletişime geçebilirsiniz.</p>
            </div>
            <div className="relative flex md:justify-end">
              <a href="/iletisim" className="inline-flex items-center gap-2 bg-gold text-ink-700 rounded-full px-6 h-14 font-semibold hover:bg-gold-400 transition-colors" data-testid="press-contact-cta">
                Bizimle iletişime geç <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Video lightbox */}
      {videoId && (
        <div
          className="fixed inset-0 z-[100] bg-ink-900/95 backdrop-blur-sm grid place-items-center p-4 md:p-8"
          onClick={() => setVideoId(null)}
          data-testid="video-lightbox"
        >
          <button
            onClick={() => setVideoId(null)}
            className="absolute top-5 right-5 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white grid place-items-center transition-colors"
            aria-label="Kapat"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="w-full max-w-5xl aspect-video rounded-2xl overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <iframe
              src={`https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`}
              title="YEKÜV Video"
              className="w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        </div>
      )}

      {/* Image lightbox with zoom & pan */}
      {lightbox?.type === 'image' && (
        <ZoomLightbox
          clip={clips[lightbox.idx]}
          index={lightbox.idx}
          total={clips.length}
          onClose={closeLightbox}
          onPrev={prevImage}
          onNext={nextImage}
        />
      )}
    </>
    </EditRegion>
  );
}

/** Fullscreen lightbox with zoom (+/− buttons, wheel, click-to-zoom) and drag-to-pan. */
function ZoomLightbox({ clip, index, total, onClose, onPrev, onNext }) {
  const [zoom, setZoom] = useState(1);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const dragRef = React.useRef(null);

  const clampZoom = (z) => Math.min(4, Math.max(1, z));
  const changeZoom = (z) => {
    const nz = clampZoom(z);
    setZoom(nz);
    if (nz === 1) setPos({ x: 0, y: 0 });
  };

  useEffect(() => { setZoom(1); setPos({ x: 0, y: 0 }); }, [index]);

  const onPointerDown = (e) => {
    if (zoom === 1) return;
    dragRef.current = { sx: e.clientX, sy: e.clientY, ox: pos.x, oy: pos.y };
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e) => {
    if (!dragRef.current) return;
    setPos({ x: dragRef.current.ox + (e.clientX - dragRef.current.sx), y: dragRef.current.oy + (e.clientY - dragRef.current.sy) });
  };
  const onPointerUp = () => { dragRef.current = null; };

  return (
    <div
      className="fixed inset-0 z-[100] bg-ink-900/95 backdrop-blur-sm grid place-items-center p-4 md:p-10"
      onClick={onClose}
      onWheel={(e) => changeZoom(zoom + (e.deltaY < 0 ? 0.4 : -0.4))}
      data-testid="image-lightbox"
    >
      <button
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        className="absolute top-5 right-5 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white grid place-items-center transition-colors z-10"
        aria-label="Kapat"
      >
        <X className="w-5 h-5" />
      </button>
      <button
        onClick={(e) => { e.stopPropagation(); onPrev(); }}
        className="absolute left-5 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white grid place-items-center transition-colors z-10"
        aria-label="Önceki"
      >
        <ChevronLeft className="w-6 h-6" />
      </button>
      <button
        onClick={(e) => { e.stopPropagation(); onNext(); }}
        className="absolute right-5 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white grid place-items-center transition-colors z-10"
        aria-label="Sonraki"
      >
        <ChevronRight className="w-6 h-6" />
      </button>

      {/* Zoom controls */}
      <div className="absolute bottom-5 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2 bg-white/10 backdrop-blur rounded-full px-3 py-2" onClick={(e) => e.stopPropagation()}>
        <button onClick={() => changeZoom(zoom - 0.5)} data-testid="lightbox-zoom-out" aria-label="Uzaklaştır"
          className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/25 text-white grid place-items-center text-xl font-bold disabled:opacity-40" disabled={zoom <= 1}>−</button>
        <span className="text-white text-xs font-semibold tabular w-12 text-center">%{Math.round(zoom * 100)}</span>
        <button onClick={() => changeZoom(zoom + 0.5)} data-testid="lightbox-zoom-in" aria-label="Yakınlaştır"
          className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/25 text-white grid place-items-center text-xl font-bold disabled:opacity-40" disabled={zoom >= 4}>+</button>
        {zoom > 1 && (
          <button onClick={() => changeZoom(1)} className="ml-1 h-10 px-4 rounded-full bg-white/10 hover:bg-white/25 text-white text-xs font-semibold">Sıfırla</button>
        )}
      </div>

      <div className="max-w-5xl max-h-[85vh] flex flex-col items-center overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="overflow-hidden rounded-lg" style={{ touchAction: 'none' }}>
          <img
            src={clip.src}
            alt={clip.title}
            onClick={() => changeZoom(zoom === 1 ? 2.5 : 1)}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            draggable={false}
            className="max-w-full max-h-[70vh] object-contain shadow-2xl select-none"
            style={{
              transform: `translate(${pos.x}px, ${pos.y}px) scale(${zoom})`,
              transition: dragRef.current ? 'none' : 'transform 0.25s ease',
              cursor: zoom === 1 ? 'zoom-in' : 'grab',
            }}
          />
        </div>
        <div className="mt-4 text-center text-cream">
          <div className="text-[10px] uppercase tracking-[0.3em] text-gold font-semibold">{clip.outlet} · {clip.year}</div>
          <div className="font-display text-xl mt-1">{clip.title}</div>
          <div className="text-cream/60 text-xs mt-1 tabular">{index + 1} / {total} · Yakınlaştırmak için görsele tıklayın veya + kullanın</div>
        </div>
      </div>
    </div>
  );
}
